import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Download, Calendar, AlertCircle, Eye, Trash2, Filter, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Saved projects state
  const [allConfigs, setAllConfigs] = useState([]);
  const [filteredConfigs, setFilteredConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [distributors, setDistributors] = useState([]);
  
  // Filters
  const [selectedDistributor, setSelectedDistributor] = useState("all");
  const [selectedRole, setSelectedRole] = useState("all");
  
  // Bulk delete
  const [selectedConfigs, setSelectedConfigs] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchAllConfigurations();
    fetchDistributors();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedDistributor, selectedRole, allConfigs]);

  const fetchAllConfigurations = async () => {
    try {
      const response = await axios.get(`${API}/admin/configurations`, { headers: getAuthHeaders() });
      setAllConfigs(response.data);
    } catch (err) {
      console.error("Failed to fetch configurations:", err);
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await axios.get(`${API}/distributors`);
      setDistributors(response.data.distributors);
    } catch (err) {
      console.error("Failed to fetch distributors:", err);
    }
  };

  const applyFilters = () => {
    let filtered = [...allConfigs];

    // Filter by distributor
    if (selectedDistributor !== "all") {
      filtered = filtered.filter(config => config.distributor === selectedDistributor);
    }

    // Filter by role
    if (selectedRole !== "all") {
      filtered = filtered.filter(config => config.user_role === selectedRole);
    }

    setFilteredConfigs(filtered);
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    try {
      await axios.delete(`${API}/configurations/${configId}`, { headers: getAuthHeaders() });
      fetchAllConfigurations();
      setSuccess("Project deleted successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to delete project");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedConfigs.length === 0) return;
    
    const count = selectedConfigs.length;
    if (!window.confirm(`Delete ${count} selected project${count > 1 ? 's' : ''}?`)) return;
    
    setDeleting(true);
    setError("");
    
    try {
      await Promise.all(
        selectedConfigs.map(id =>
          axios.delete(`${API}/configurations/${id}`, { headers: getAuthHeaders() })
        )
      );
      
      setSelectedConfigs([]);
      fetchAllConfigurations();
      setSuccess(`Successfully deleted ${count} project${count > 1 ? 's' : ''}`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error('Error deleting projects:', err);
      setError("Error deleting some projects. Please try again.");
      setTimeout(() => setError(""), 3000);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectConfig = (id) => {
    setSelectedConfigs(prev =>
      prev.includes(id)
        ? prev.filter(configId => configId !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedConfigs.length === filteredConfigs.length && filteredConfigs.length > 0) {
      setSelectedConfigs([]);
    } else {
      setSelectedConfigs(filteredConfigs.map(c => c.id));
    }
  };

  const handleDownloadPDF = async (config) => {
    try {
      setError("");
      const response = await axios.get(
        `${API}/configurations/${config.id}/download-pdf`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quote_${config.project_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Untitled'}_${new Date(config.created_at).toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('PDF downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF: ' + (err.response?.data?.detail || err.message));
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleExportCSV = async (detailed = false) => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const endpoint = detailed ? 'quotes-detailed-csv' : 'quotes-csv';
      const response = await axios.get(
        `${API}/admin/reports/${endpoint}`,
        {
          params: { start_date: startDate, end_date: endDate },
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = detailed 
        ? `quotes_detailed_${startDate}_to_${endDate}.csv`
        : `quotes_report_${startDate}_to_${endDate}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSuccess(`${detailed ? 'Detailed report' : 'Summary report'} downloaded successfully`);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Back to Dashboard Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <CardTitle className="text-2xl">Quotes Reports</CardTitle>
                <CardDescription>Export quotes data for a specific date range</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="bg-green-50 text-green-800 border-green-200">
                <Download className="h-4 w-4" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">
                  <Calendar className="inline h-4 w-4 mr-2" />
                  Start Date
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end-date">
                  <Calendar className="inline h-4 w-4 mr-2" />
                  End Date
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={() => handleExportCSV(false)}
                disabled={loading || !startDate || !endDate}
                className="bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                <Download className="h-5 w-5 mr-2" />
                {loading ? "Generating..." : "Summary Report"}
              </Button>
              <Button
                onClick={() => handleExportCSV(true)}
                disabled={loading || !startDate || !endDate}
                className="bg-green-600 hover:bg-green-700"
                size="lg"
              >
                <FileText className="h-5 w-5 mr-2" />
                {loading ? "Generating..." : "Detailed Report (with BOM)"}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-sm text-slate-700 mb-2">Summary Report:</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Project Name & Quote Reference</li>
                  <li>• User Email & Company</li>
                  <li>• Quote Value & Status</li>
                  <li>• Created & Last Updated Dates</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-sm text-green-700 mb-2">Detailed Report (BOM):</h3>
                <ul className="text-sm text-green-600 space-y-1">
                  <li>• All summary fields plus:</li>
                  <li>• SKU & Description per item</li>
                  <li>• Quantity & Unit Price</li>
                  <li>• Line Totals</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* All Saved Projects Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">All Saved Projects ({filteredConfigs.length})</CardTitle>
                <CardDescription>View and manage all quotes with filters</CardDescription>
              </div>
              {filteredConfigs.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="select-all-projects"
                      checked={selectedConfigs.length === filteredConfigs.length && filteredConfigs.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                    <label htmlFor="select-all-projects" className="text-sm text-slate-600 cursor-pointer">
                      Select All
                    </label>
                  </div>
                  {selectedConfigs.length > 0 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleBulkDelete}
                      disabled={deleting}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Selected ({selectedConfigs.length})
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="distributor-filter">
                  <Filter className="inline h-4 w-4 mr-2" />
                  Filter by Distributor
                </Label>
                <Select value={selectedDistributor} onValueChange={setSelectedDistributor}>
                  <SelectTrigger id="distributor-filter">
                    <SelectValue placeholder="All Distributors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Distributors</SelectItem>
                    {distributors.map((dist) => (
                      <SelectItem key={dist} value={dist}>{dist}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role-filter">
                  <Filter className="inline h-4 w-4 mr-2" />
                  Filter by User Role
                </Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger id="role-filter">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="enduser">End User</SelectItem>
                    <SelectItem value="installer">Installer</SelectItem>
                    <SelectItem value="distributor">Distributor</SelectItem>
                    <SelectItem value="distributor_admin">Distributor Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Projects Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="w-12 py-3 px-2"></th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700">Project Name</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700">User</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700">Role</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700">Distributor</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700">Value</th>
                    <th className="text-left py-3 px-2 font-semibold text-slate-700">Created</th>
                    <th className="text-right py-3 px-2 font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConfigs.map((config) => (
                    <tr 
                      key={config.id} 
                      className={`border-b border-slate-100 hover:bg-slate-50 ${
                        selectedConfigs.includes(config.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="py-3 px-2">
                        <Checkbox
                          id={`config-${config.id}`}
                          checked={selectedConfigs.includes(config.id)}
                          onCheckedChange={() => toggleSelectConfig(config.id)}
                        />
                      </td>
                      <td className="py-3 px-2 font-medium text-slate-900">
                        {config.project_name || 'Untitled'}
                      </td>
                      <td className="py-3 px-2 text-slate-600">{config.user_email}</td>
                      <td className="py-3 px-2 text-slate-600 capitalize">
                        {config.user_role?.replace('_', ' ') || '-'}
                      </td>
                      <td className="py-3 px-2 text-slate-600">{config.distributor || '-'}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          config.status === 'won' ? 'bg-green-100 text-green-800' :
                          config.status === 'lost' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {config.status || 'active'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-slate-900 font-medium">
                        £{config.final_total?.toFixed(2) || '0.00'}
                      </td>
                      <td className="py-3 px-2 text-slate-600">
                        {config.created_at?.substring(0, 10) || '-'}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedConfig(config)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {/* Download PDF button removed */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteConfig(config.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredConfigs.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No projects found matching the current filters
              </div>
            )}
          </CardContent>
        </Card>

        {/* Project Details Modal */}
        {selectedConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>{selectedConfig.project_name || 'Project Details'}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4"
                  onClick={() => setSelectedConfig(null)}
                >
                  ✕
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Quote Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-xs text-slate-500">User</p>
                      <p className="text-sm font-medium">{selectedConfig.user_email}</p>
                    </div>
                    {selectedConfig.quote_reference && (
                      <div>
                        <p className="text-xs text-slate-500">Quote Ref</p>
                        <p className="text-sm font-medium">{selectedConfig.quote_reference}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-slate-500">Category</p>
                      <p className="text-sm font-medium capitalize">
                        {selectedConfig.configurator_type === 'parking_barrier' ? 'Parking Barriers' :
                         selectedConfig.configurator_type === 'full_height_turnstiles' ? 'Full Height Turnstiles' :
                         selectedConfig.configurator_type === 'atlas' ? 'Atlas Access Control' :
                         'Comet Speedlane'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Total Value</p>
                      <p className="text-sm font-bold text-green-600">£{selectedConfig.final_total?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Created</p>
                      <p className="text-sm">{new Date(selectedConfig.created_at).toLocaleDateString('en-GB')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Status</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        selectedConfig.status === 'won' ? 'bg-green-100 text-green-800' :
                        selectedConfig.status === 'lost' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {selectedConfig.status || 'active'}
                      </span>
                    </div>
                  </div>

                  {/* Bill of Materials */}
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-3">Bill of Materials</h3>
                    {(selectedConfig.combined_bom || selectedConfig.bom)?.length > 0 ? (
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left py-2 px-3 font-semibold">SKU</th>
                              <th className="text-left py-2 px-3 font-semibold">Description</th>
                              <th className="text-center py-2 px-3 font-semibold">Qty</th>
                              <th className="text-right py-2 px-3 font-semibold">Unit Price</th>
                              <th className="text-right py-2 px-3 font-semibold">Line Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedConfig.combined_bom || selectedConfig.bom).map((item, idx) => (
                              <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-mono text-xs">{item.sku}</td>
                                <td className="py-2 px-3">{item.description}</td>
                                <td className="py-2 px-3 text-center">{item.quantity}</td>
                                <td className="py-2 px-3 text-right">£{item.unit_price?.toFixed(2)}</td>
                                <td className="py-2 px-3 text-right font-medium">£{item.line_total?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50 font-semibold">
                            <tr>
                              <td colSpan="4" className="py-2 px-3 text-right">Subtotal:</td>
                              <td className="py-2 px-3 text-right">
                                £{(selectedConfig.combined_bom || selectedConfig.bom)
                                  .reduce((sum, item) => sum + (item.line_total || 0), 0)
                                  .toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500 bg-slate-50 p-4 rounded-lg">
                        <p>No BOM details available for this quote.</p>
                        <p className="text-xs mt-1">BOM data is saved for quotes created after the system update.</p>
                      </div>
                    )}
                  </div>

                  {/* Configuration Summary */}
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-3">Configuration Summary</h3>
                    <div className="text-sm bg-slate-50 p-4 rounded-lg">
                      {selectedConfig.configurator_type === 'parking_barrier' && selectedConfig.lanes && (
                        <div className="space-y-1">
                          <p><span className="font-medium">Lanes:</span> {selectedConfig.lanes.length}</p>
                          {selectedConfig.lanes.length > 0 && (
                            <>
                              <p><span className="font-medium">Chassis:</span> {selectedConfig.lanes[0].chassisType}</p>
                              <p><span className="font-medium">Boom:</span> {selectedConfig.lanes[0].boomLength}m</p>
                              <p><span className="font-medium">LED:</span> {selectedConfig.lanes[0].hasLED ? 'Yes' : 'No'}</p>
                            </>
                          )}
                        </div>
                      )}
                      {selectedConfig.configurator_type === 'full_height_turnstiles' && (
                        <div className="space-y-1">
                          <p><span className="font-medium">Type:</span> {selectedConfig.turnstile_type}</p>
                          <p><span className="font-medium">Arms:</span> {selectedConfig.arm_config}</p>
                          <p><span className="font-medium">Cover:</span> {selectedConfig.cover_type}</p>
                          <p><span className="font-medium">Quantity:</span> {selectedConfig.quantity}</p>
                        </div>
                      )}
                      {selectedConfig.banks && (
                        <div className="space-y-1">
                          <p><span className="font-medium">Banks:</span> {selectedConfig.banks.length}</p>
                          {selectedConfig.banks.map((bank, idx) => (
                            <p key={idx} className="ml-4">
                              Bank {bank.bank_number}: {bank.total_lanes} lanes ({bank.dda_lanes} DDA)
                            </p>
                          ))}
                        </div>
                      )}
                      {selectedConfig.total_doors && (
                        <div className="space-y-1">
                          <p><span className="font-medium">Doors:</span> {selectedConfig.total_doors}</p>
                          <p><span className="font-medium">Controllers:</span> {selectedConfig.total_controllers}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
